<template>
  <span class="adjustment-input-container">
    <!-- 편집 모드 -->
    <template v-if="isEditMode">
      <div class="adjustment-input-wrapper adjustment-input-active">
        <a-input-number
          ref="inputRef"
          v-model:value="localValue"
          size="small"
          :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
          :parser="value => value.replace(/\+/g, '')"
          @keydown="handleKeydown"
        />
        <CheckOutlined class="input-action-icon confirm" @click="handleConfirm" />
        <CloseOutlined class="input-action-icon cancel" @click="handleCancel" />
      </div>
    </template>

    <!-- 표시 모드 -->
    <template v-else>
      <span
        class="editable-value"
        :class="{ 'has-adjustment': hasAdjustmentValue }"
        @click="handleStartEdit"
      >
        <span class="value-text">{{ displayValue }}</span>
        <span v-if="hasAdjustmentValue" class="adjustment-indicator">
          ({{ adjustmentValue >= 0 ? '+' : '' }}{{ adjustmentValue }})
        </span>
        <EditOutlined class="edit-icon" />
      </span>
    </template>
  </span>
</template>

<script>
import { defineComponent, ref, computed, watch, nextTick } from 'vue';
import { InputNumber } from 'ant-design-vue';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons-vue';

export default defineComponent({
  name: 'AdjustmentInput',
  components: {
    AInputNumber: InputNumber,
    CheckOutlined,
    CloseOutlined,
    EditOutlined,
  },
  props: {
    // 원본 값 (서버에서 가져온 기본 값)
    baseValue: {
      type: Number,
      default: 0,
    },
    // 현재 보정치 값
    adjustmentValue: {
      type: Number,
      default: 0,
    },
    // 현재 편집 중인지 여부 (외부에서 제어)
    isEditing: {
      type: Boolean,
      default: false,
    },
    // 숫자 포맷팅 함수
    formatter: {
      type: Function,
      default: (value) => {
        if (value === null || value === undefined) return '-';
        return value.toLocaleString();
      },
    },
  },
  emits: ['start-edit', 'confirm', 'cancel'],
  setup(props, { emit }) {
    const inputRef = ref(null);
    const localValue = ref(0);

    // 편집 모드 여부
    const isEditMode = computed(() => props.isEditing);

    // 보정치가 있는지 여부
    const hasAdjustmentValue = computed(() => props.adjustmentValue !== 0);

    // 보정된 값 (기본값 + 보정치)
    const adjustedValue = computed(() => (props.baseValue || 0) + (props.adjustmentValue || 0));

    // 화면에 표시할 값 (포맷팅된 보정 후 값)
    const displayValue = computed(() => props.formatter(adjustedValue.value));

    // 편집 모드 진입 시 현재 보정치 값 로드
    watch(
      () => props.isEditing,
      (newVal) => {
        if (newVal) {
          localValue.value = props.adjustmentValue || 0;
          nextTick(() => {
            if (inputRef.value) {
              inputRef.value.focus();
            }
          });
        }
      }
    );

    // 편집 시작
    const handleStartEdit = () => {
      emit('start-edit');
    };

    // 편집 확인
    const handleConfirm = () => {
      emit('confirm', localValue.value);
    };

    // 편집 취소
    const handleCancel = () => {
      emit('cancel');
    };

    // 키보드 이벤트
    const handleKeydown = (event) => {
      if (event.key === 'Enter') {
        handleConfirm();
      } else if (event.key === 'Escape') {
        handleCancel();
      }
    };

    return {
      inputRef,
      localValue,
      isEditMode,
      hasAdjustmentValue,
      adjustedValue,
      displayValue,
      handleStartEdit,
      handleConfirm,
      handleCancel,
      handleKeydown,
    };
  },
});
</script>

<style lang="scss" scoped>
.adjustment-input-container {
  display: inline-flex;
  align-items: center;
}

.adjustment-input-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  :deep(.ant-input-number) {
    width: 80px;

    .ant-input-number-input {
      text-align: right;
    }
  }
}

.adjustment-input-active {
  background: #fff7e6;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #ffc53d;
}

.input-action-icon {
  cursor: pointer;
  font-size: 14px;
  padding: 2px;

  &.confirm {
    color: #52c41a;

    &:hover {
      color: #73d13d;
    }
  }

  &.cancel {
    color: #ff4d4f;

    &:hover {
      color: #ff7875;
    }
  }
}

.editable-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px dashed #d9d9d9;
  background: #fafafa;
  transition: all 0.2s ease;

  &:hover {
    background: #e6f7ff;
    border-color: #1890ff;

    .edit-icon {
      color: #1890ff;
    }
  }

  &.has-adjustment {
    background: #f6ffed;
    border-color: #b7eb8f;

    .value-text {
      color: #1890ff;
      font-weight: 600;
    }
  }
}

.value-text {
  // 기본 스타일
}

.adjustment-indicator {
  color: #52c41a;
  font-size: 12px;
  font-weight: 500;
}

.edit-icon {
  color: #1890ff;
  font-size: 12px;
  opacity: 1;
  margin-left: 2px;
}
</style>
